import React, { useCallback, useMemo, useRef } from 'react';
import { selectActiveTab, useSelector } from '@splunk/dashboard-state';
import { _ } from '@splunk/ui-utils/i18n';
import { createDOMID } from '@splunk/ui-utils/id';
import { TabListMenu, useTabListMenu } from '@splunk/dashboard-ui';
import SquaresLayeredPlus from '@splunk/react-icons/SquaresLayeredPlus';
import { ActionButton, useActionButtonClick } from '../ActionButton';
import { ActionMenuPopup } from '../ActionMenuPopup';
import type { BaseButtonProps } from '../BaseButton';
import { getButtonTitle } from '../utils/getButtonTitle';

const cloneIcon = <SquaresLayeredPlus />;

export const CloneButton = ({
    itemId,
    dashboardApi,
    screenReaderText,
}: BaseButtonProps): JSX.Element | null => {
    const tabListMenuId = useMemo(() => createDOMID(), []);

    const { open, handleOpen, handleRequestClose, anchor, setRef, tabCount } =
        useTabListMenu();

    const activeTabId = useSelector(selectActiveTab);
    const activeTabIdRef = useRef(activeTabId);
    activeTabIdRef.current = activeTabId;

    const cloneToTab = useCallback(
        (toLayoutId?: string) => {
            if (typeof itemId !== 'string') {
                return;
            }
            // dashboardApi and itemId have to exist to execute this callback
            dashboardApi?.cloneDashboardItems({
                itemIds: [itemId],
                offsetMultiplier: 1,
                toLayoutId,
            });
        },
        [dashboardApi, itemId]
    );

    const onCloneToTab = useActionButtonClick('paste', cloneToTab);

    const onClick = useCallback(
        () => onCloneToTab(activeTabIdRef.current),
        [onCloneToTab]
    );

    const buttonTitle = getButtonTitle({
        base: _('Clone'),
        screenReaderText,
    });

    const handleElementRef = useCallback(
        (element: HTMLButtonElement | HTMLAnchorElement | null) => {
            setRef(element instanceof HTMLButtonElement ? element : null);
        },
        [setRef]
    );

    if (!itemId || !dashboardApi) {
        return null;
    }

    return (
        <>
            <ActionButton
                data-test="clone-button"
                onClick={tabCount > 1 ? handleOpen : onClick}
                icon={cloneIcon}
                elementRef={handleElementRef}
                title={buttonTitle}
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls={tabListMenuId}
            />
            {tabCount > 1 && (
                <ActionMenuPopup onRequestClose={handleRequestClose}>
                    <TabListMenu
                        open={open}
                        anchor={anchor}
                        onRequestClose={handleRequestClose}
                        onSelectTab={onCloneToTab}
                        heading={_('Clone to...')}
                        showActiveTab
                        tabListMenuId={tabListMenuId}
                    />
                </ActionMenuPopup>
            )}
        </>
    );
};
